/**
 * Protected Route Component
 *
 * Ensures users are authenticated and have the Auditor role before accessing protected routes.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Spinner, Alert, Container, Button } from 'react-bootstrap';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuditorRole?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuditorRole = true,
}) => {
  const { user, isAuthenticated, isLoading, error, login } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Verificando autenticación...</p>
        </div>
      </Container>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error de Autenticación</Alert.Heading>
          <p>{error.message}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for Auditor role if required
  if (requireAuditorRole && !user.isAuditor) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Acceso Denegado</Alert.Heading>
          <p>
            Tu cuenta <strong>{user.email}</strong> no tiene el rol de <strong>Auditor</strong>{' '}
            asignado.
          </p>
          <p className="mb-0">
            Por favor contacta al administrador del sistema para solicitar acceso como auditor
            médico.
          </p>
          <hr />
          <div className="d-flex gap-2">
            <Button
              variant="outline-warning"
              onClick={() => login()}
            >
              Iniciar con otra cuenta
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
